import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, FONT, px, motionMs } from '../theme';
import { layoutOf, watchResize } from '../ui/layout';
import { createScrollArea, type ScrollArea } from '../ui/scrollArea';
import { onTap } from '../ui/tap';
import { createButton } from '../ui/button';
import { createListRow } from '../ui/panel';
import { createTextInput } from '../ui/textInput';
import { Rng, generateSeed } from '@core/rng';
import {
  BACKGROUNDS,
  PORTRAIT_IDS,
  createCharacter,
  randomName,
  genderOfPortrait,
  LOST_GENDER,
  type Background,
  type Character,
} from '@core/character/character';
import { Campaign, type PrologueChoice } from '@core/campaign/campaign';
import { DIFFICULTIES, DEFAULT_DIFFICULTY, type Difficulty } from '@core/campaign/difficulty';
import { drawFallbackPortrait } from '../render/portrait';
import { portraitKey } from '../assets';
import type { MapData } from '../mapData';

/**
 * Charaktererstellung.
 *
 * Statt sechs Reglern auf gezeichneten Ebenen steht hier ein Raster fertiger
 * Portraets. Der Grund ist nicht Bequemlichkeit: Die Regler sahen nach
 * Platzhalter aus, und der Editor ist das Erste, was man vom Spiel sieht.
 * Fertige Aufnahmen kosten dafuer die freie Kombinierbarkeit — bei einer Figur,
 * die man im Spiel fast nie sieht, ist das der bessere Handel.
 *
 * Die Herkunft ist dagegen kein Beiwerk. Sie steht mit ihrer Wirkung und ihrem
 * Verlust ausgeschrieben da — beides begleitet die Figur bis ins Finale.
 */
export class CharacterCreatorScene extends Phaser.Scene {
  private character!: Character;
  /** Der gewaehlte Schwierigkeitsgrad. Bleibt beim Auswuerfeln der Figur stehen. */
  private difficulty: Difficulty = DEFAULT_DIFFICULTY;
  /**
   * Hat die Spielerin den Namen selbst eingetippt? Dann bleibt er, auch wenn
   * sie ein anderes Gesicht waehlt. Sonst folgt der Vorschlag dem Portraet.
   */
  private nameEdited = false;
  private scroll: ScrollArea | null = null;
  /** Scrollstand ueber Neuaufbauten hinweg — sonst spraenge die Seite nach
   * jeder Wahl zurueck nach oben, weg von dem, was man gerade angetippt hat. */
  private scrollOffset = 0;

  constructor() {
    super('CharacterCreator');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.abyss);
    this.character = createCharacter(Rng.fromSeed(generateSeed()));
    this.nameEdited = false;
    this.scroll = null;
    this.scrollOffset = 0;
    this.redraw();
    watchResize(this, () => this.redraw());
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      // Enter beginnt — ausser im Namensfeld, das faengt seine Tasten selbst ab.
      if (event.key === 'Enter') this.begin();
    });
    this.cameras.main.fadeIn(motionMs(320), 5, 8, 13);
  }

  private update_(changes: Partial<Character>): void {
    this.character = { ...this.character, ...changes };
    this.redraw();
  }

  /**
   * Zwei Spalten nebeneinander, wenn Platz ist — sonst untereinander.
   *
   * Frueher standen sie immer nebeneinander, mit `width / 2 - 60` je Spalte:
   * Auf einem Telefon im Hochformat blieben 135 Pixel, die Portraets wurden
   * briefmarkengross und die Hintergruende liefen ineinander. Alles liegt
   * jetzt in einem Scrollbereich; die Knoepfe bleiben unten stehen.
   */
  private redraw(): void {
    if (this.scroll !== null) this.scrollOffset = this.scroll.offset();
    this.scroll?.destroy();
    this.children.removeAll(true);

    const layout = layoutOf(this);
    const { width, height, centreX } = layout;
    const stacked = layout.compact;

    const title = this.add
      .text(centreX, 18, t('character.title'), titleStyle(stacked ? 20 : 24))
      .setOrigin(0.5, 0);
    const top = title.y + title.height + 14;
    const footer = 44 + 32;

    // Alles Folgende entsteht in Inhaltskoordinaten und wandert danach in den
    // Scrollbereich — so bleiben die Zeichenfunktionen, wie sie waren.
    const before = new Set(this.children.list);

    let contentWidth: number;
    if (stacked) {
      contentWidth = Math.min(480, width - layout.margin * 2);
      const gridBottom = this.renderPortraitGrid(0, 0, contentWidth);
      const nameBottom = this.renderName(0, gridBottom + 20, contentWidth);
      this.renderBackground(0, nameBottom + 20, contentWidth);
    } else {
      // Breit genug, dass ein Gesicht im Raster erkennbar bleibt. Bei 340 px
      // waren die Kacheln 63 px breit — man konnte sie nicht unterscheiden.
      const columnWidth = Math.min(430, width / 2 - 60);
      contentWidth = columnWidth * 2 + 64;
      const gridBottom = this.renderPortraitGrid(0, 0, columnWidth);
      this.renderName(0, gridBottom + 24, columnWidth);
      this.renderBackground(columnWidth + 64, 0, columnWidth);
    }

    const created = this.children.list.filter((child) => !before.has(child));
    this.scroll = createScrollArea(this, {
      x: centreX - contentWidth / 2,
      y: top,
      width: contentWidth,
      height: height - top - footer,
    });
    this.scroll.content.add(created);
    this.scroll.finish();
    this.scroll.scrollTo(this.scrollOffset);

    const buttonWidth = Math.min(190, (width - 48) / 2);
    createButton(this, centreX - buttonWidth / 2 - 8, height - 38, t('character.randomise'), () => {
      this.character = createCharacter(Rng.fromSeed(generateSeed()));
      this.nameEdited = false;
      this.redraw();
    }, { width: buttonWidth, fontSize: stacked ? 12 : 15 });

    createButton(this, centreX + buttonWidth / 2 + 8, height - 38, t('character.begin'), () => this.begin(), {
      width: buttonWidth,
      fontSize: stacked ? 12 : 15,
    });
  }

  /**
   * Das Auswahlraster.
   *
   * Fuenf Spalten, drei Reihen — alle fuenfzehn Portraets auf einen Blick, ohne
   * Blaettern. Das Gewaehlte bekommt einen hellen Rahmen; die uebrigen liegen
   * leicht abgedunkelt darunter, damit die Wahl auch ohne Farbe lesbar ist.
   */
  private renderPortraitGrid(left: number, top: number, columnWidth: number): number {
    this.add.text(left, top, t('character.portrait'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });

    const columns = 5;
    const gap = 6;
    const cell = Math.floor((columnWidth - gap * (columns - 1)) / columns);
    const cellHeight = Math.round(cell * 1.25);

    PORTRAIT_IDS.forEach((id, index) => {
      const x = left + (index % columns) * (cell + gap);
      const y = top + 20 + Math.floor(index / columns) * (cellHeight + gap);
      const chosen = this.character.portraitId === id;

      const image = this.add
        .image(x, y, this.textureFor(id))
        .setOrigin(0, 0)
        .setDisplaySize(cell, cellHeight)
        .setAlpha(chosen ? 1 : 0.55)
        .setInteractive({ useHandCursor: true });

      onTap(image, {
        onTap: () => this.choosePortrait(id),
        onHighlight: (on) => {
          if (!chosen) image.setAlpha(on ? 0.85 : 0.55);
        },
      });

      this.add
        .rectangle(x, y, cell, cellHeight)
        .setOrigin(0, 0)
        .setStrokeStyle(chosen ? 2 : 1, chosen ? PALETTE.coast : PALETTE.panelEdge);
    });

    const rows = Math.ceil(PORTRAIT_IDS.length / columns);
    return top + 20 + rows * (cellHeight + gap) - gap;
  }

  /**
   * Der Texturschluessel eines Portraets — oder ein gezeichneter Ersatz.
   *
   * Ohne diesen Rueckfall bliebe der Editor leer, sobald die Bilddateien
   * fehlen, etwa in einem frischen Arbeitsverzeichnis, in dem
   * `tools/fetch_assets.py` noch nicht gelaufen ist.
   */
  private textureFor(id: string): string {
    const key = portraitKey(id);
    if (this.textures.exists(key)) return key;

    const fallback = `portrait-fallback:${id}`;
    if (!this.textures.exists(fallback)) {
      this.textures.addCanvas(fallback, drawFallbackPortrait(id, 128));
    }
    return fallback;
  }

  /** Liefert die Unterkante. */
  private renderName(left: number, y: number, columnWidth: number): number {
    this.add.text(left, y, t('character.name'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });

    // Eingabefeld statt fester Anzeige: Der eigene Name gehoert der Spielerin.
    // Der Wuerfel bleibt daneben, fuer alle, die nicht tippen wollen.
    createTextInput(this, left, y + 18, {
      width: columnWidth - 150,
      value: this.character.name,
      // Nicht `update_` verwenden: Ein Neuzeichnen bei jedem Tastendruck
      // wuerde das Feld und damit die Eingabe verlieren.
      onChange: (name) => {
        this.character = { ...this.character, name };
        this.nameEdited = true;
      },
    });

    createButton(
      this,
      left + columnWidth - 68,
      y + 18 + 22,
      t('character.name.reroll'),
      () => {
        this.nameEdited = false;
        this.update_({ name: this.suggestedName(this.character.portraitId) });
      },
      { width: 130, height: 44, fontSize: 10 },
    );
    return y + 18 + 44;
  }

  private renderBackground(left: number, top: number, columnWidth: number): void {
    this.add.text(left, top, t('character.background'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });

    // Die Zeilen wachsen mit ihrem Text; jede beginnt unter der vorigen statt
    // auf festen 92 Pixeln.
    let y = top + 22;
    for (const background of BACKGROUNDS) {
      const row = createListRow(this, left, y, {
        width: columnWidth,
        height: 84,
        title: t(`background.${background}`),
        detail: t(`background.${background}.blurb`),
        selected: this.character.background === background,
        onClick: () => this.chooseBackground(background),
      });
      y += row.height + 8;
    }
    y += 4;

    const effect = this.add.text(left, y, t(`background.${this.character.background}.effect`), {
      ...readoutStyle(11),
      color: TEXT_COLOUR.base,
      wordWrap: { width: columnWidth },
    });

    // Der Verlust ist der Grund, warum die Figur überhaupt hier draußen ist.
    const lost = this.add.text(
      left,
      y + effect.height + 10,
      t('character.lost', {
        name: this.character.lostName,
        relation: t(this.character.lostRelationKey),
      }),
      { ...readoutStyle(11), color: PALETTE.inkFaint, fontStyle: 'italic', wordWrap: { width: columnWidth } },
    );

    this.renderDifficulty(left, lost.y + lost.height + 24, columnWidth);
  }

  /**
   * Der Schwierigkeitsgrad — drei Zeilen, jede mit dem, was sie bedeutet.
   *
   * Steht bei der Figur und nicht im Menue: Er gehoert zu genau diesem
   * Durchlauf, wie Herkunft und Verlust.
   */
  private renderDifficulty(left: number, top: number, columnWidth: number): void {
    this.add.text(left, top, t('difficulty.title'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });

    let y = top + 22;
    for (const difficulty of DIFFICULTIES) {
      const row = createListRow(this, left, y, {
        width: columnWidth,
        height: 52,
        title: t(`difficulty.${difficulty}`),
        detail: t(`difficulty.${difficulty}.blurb`),
        selected: this.difficulty === difficulty,
        onClick: () => {
          this.difficulty = difficulty;
          this.redraw();
        },
      });
      y += row.height + 8;
    }
  }

  private chooseBackground(background: Background): void {
    this.update_({
      background,
      // Der Verlust haengt am Hintergrund und wechselt mit ihm — und mit ihm
      // das Geschlecht der verlorenen Person, passend zu „Rottenflieger“,
      // „Bootsmann“ oder „Mutter“.
      lostRelationKey: createCharacter(Rng.fromSeed('x'), { background }).lostRelationKey,
      lostName: randomName(Rng.fromSeed(generateSeed()), LOST_GENDER[background]),
    });
  }

  /** Ein Name, der zum Gesicht auf dem Portraet passt. */
  private suggestedName(portraitId: string): string {
    return randomName(Rng.fromSeed(generateSeed()), genderOfPortrait(portraitId));
  }

  /**
   * Ein anderes Portraet. Solange der Name nur ein Vorschlag ist, folgt er dem
   * Gesicht — sonst stuende unter einer Frau ein Maennername und umgekehrt.
   */
  private choosePortrait(portraitId: string): void {
    const rename =
      !this.nameEdited && genderOfPortrait(portraitId) !== genderOfPortrait(this.character.portraitId);
    this.update_(rename ? { portraitId, name: this.suggestedName(portraitId) } : { portraitId });
  }

  private begin(): void {
    // Wer das Feld leert, bekommt wieder einen zufaelligen Namen — ohne Namen
    // laesst sich im Finale schlecht ueber jemanden sprechen.
    if (this.character.name.trim() === '') {
      this.character = { ...this.character, name: this.suggestedName(this.character.portraitId) };
    }

    const mapData = this.registry.get('mapData') as MapData;
    const seed = (this.registry.get('pendingSeed') as string | undefined) ?? generateSeed();

    const campaign = new Campaign({
      seed,
      bathymetry: mapData.bathymetry,
      character: this.character,
      difficulty: this.difficulty,
      // Die gezeichnete Kueste geht in die Wegsuche ein — sonst faehrt das
      // Schiff ueber Land, das auf der Karte deutlich sichtbar ist.
      coastline: mapData.coastline,
    });

    // Die Wahl aus dem Prolog ist der erste Einsatz der Kampagne. Wer den
    // Prolog uebersprungen hat, beginnt ohne sie.
    const prologue = this.registry.get('prologueChoice') as PrologueChoice | undefined;
    if (prologue !== undefined) campaign.applyPrologue(prologue);
    this.registry.remove('prologueChoice');

    this.registry.set('campaign', campaign);
    // Gespeichert wird an der Station, nicht hier — der Introbildschirm liegt
    // dazwischen, und ein Stand mittendrin haette keinen Wert.
    this.scene.start('Intro');
  }
}
